import { useT } from "@/i18n";
import {
  AnswerLine,
  FeedbackBox,
  Hero,
  PromptImage,
  PromptVideo,
  ReadOnlyCard,
  Section,
  SpeakerButton,
} from "./ReadOnlyKit";
import { rs } from "./readOnlyStyles";
import type { ReadOnlyTemplateProps } from "./types";
import { useReviewAudio } from "./useReviewAudio";

/** A past listen-and-repeat play: what the student said, what was asked for, and the coaching. */
export function ListenRepeatReadOnly({
  snapshot,
  response,
  score,
  recordingUrl,
}: ReadOnlyTemplateProps<"listenRepeat">) {
  const t = useT();
  const { correctAnswer, instructionText, mediaType, image, video } = snapshot.slideData;
  const { playingUrl, toggle } = useReviewAudio();

  const myRecording = recordingUrl(response.take?.recordingId);
  const expected = response.expectedAnswer || correctAnswer;

  const myRecordingSpeaker = (
    <SpeakerButton
      label={t["readonly.myRecording"]}
      url={myRecording}
      isPlaying={playingUrl === myRecording}
      onPress={() => toggle(myRecording)}
      variant="student"
    />
  );

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      <Hero>
        {instructionText ? <p style={rs.heroSubtext}>{instructionText}</p> : null}
        {mediaType === "video" ? (
          <PromptVideo media={video} label={t["readonly.promptVideo"]} />
        ) : (
          <PromptImage media={image} label={t["readonly.promptImage"]} />
        )}
      </Hero>

      <Section label={t["readonly.myAnswer"]}>
        {/* A recordOnly play was never transcribed or scored, so the speaker stands on its own.
            A typed answer has no recording at all, so it stands alone the other way round. */}
        {response.take?.recognizedText ? (
          <AnswerLine
            studentAnswer={response.take.recognizedText}
            score={response.gradedScore ?? response.take.scores?.accuracyScore ?? null}
            accessory={myRecording ? myRecordingSpeaker : undefined}
          />
        ) : (
          myRecordingSpeaker
        )}
      </Section>

      <Section label={t["readonly.correctAnswer"]}>
        <span style={rs.answerText}>{expected || "—"}</span>
      </Section>

      {response.explanation ? <FeedbackBox text={response.explanation} /> : null}
    </ReadOnlyCard>
  );
}
